import * as Sentry from '@sentry/react';
import { match } from 'ts-pattern';
import { z } from 'zod';

import type { ValidationItem } from './validation.utils';

/**
 * The minimal shape of a repositioned field the highlight logic reads. Matches
 * the entries in a `GetIngestionDocumentFragment.fields` record (keyed by
 * contract document uuid) — `id` is the field uuid, `mappingKey` links it to a
 * validation result, and `pageNumber` scopes page-specific failures.
 */
export interface RepositionedFieldLike {
  id?: string | null;
  mappingKey?: string | null;
  fieldType?: unknown;
  label?: unknown;
  pageNumber?: number | null;
  /**
   * Page of the uploaded PDF the field was read from. Differs from
   * `pageNumber` when the upload is missing document version pages, and is
   * absent on extractions that predate it.
   */
  ingestedPageNumber?: number | null;
  x?: number | null;
  y?: number | null;
  width?: number | null;
  height?: number | null;
}

/** A document's repositioned fields, keyed by contract document uuid. */
export type FieldsByContractDocument = Record<string, RepositionedFieldLike[]>;

const repositionedFieldSchema = z
  .object({
    id: z.string().nullish(),
    mappingKey: z.string().nullish(),
    pageNumber: z.number().nullish(),
    ingestedPageNumber: z.number().nullish(),
    x: z.number().nullish(),
    y: z.number().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
  })
  .passthrough();

const fieldsByContractDocumentSchema = z.record(z.string(), z.unknown());

/** Validates persisted extraction JSON before compliance surfaces traverse it. */
export const parseFieldsByContractDocument = (
  value: unknown
): FieldsByContractDocument => {
  // Absent fields is a legitimate state (no document selected, query still
  // loading, nullable GraphQL field) — not malformed data.
  if (value == null) {
    return {};
  }

  const result = fieldsByContractDocumentSchema.safeParse(value);
  if (!result.success) {
    Sentry.captureMessage('Ignored malformed persisted document fields', {
      extra: { valueType: typeof value },
      level: 'warning',
    });

    return {};
  }

  const fieldsByContractDocument: FieldsByContractDocument = {};
  let invalidEntryCount = 0;

  for (const [contractDocumentUuid, fields] of Object.entries(result.data)) {
    if (!Array.isArray(fields)) {
      invalidEntryCount += 1;
      continue;
    }

    fieldsByContractDocument[contractDocumentUuid] = fields.flatMap((field) => {
      const parsedField = repositionedFieldSchema.safeParse(field);
      if (!parsedField.success) {
        invalidEntryCount += 1;

        return [];
      }

      return [parsedField.data];
    });
  }

  if (invalidEntryCount > 0) {
    Sentry.captureMessage('Ignored malformed persisted document fields', {
      extra: { invalidEntryCount },
      level: 'warning',
    });
  }

  return fieldsByContractDocument;
};

const asFieldArray = (value: unknown): RepositionedFieldLike[] =>
  Array.isArray(value) ? (value as RepositionedFieldLike[]) : [];

/**
 * The uploaded-PDF page a field was read from — the only page number that can
 * be compared against a `ValidationItem.pageNumbers` entry or used to place an
 * overlay, since both index the uploaded PDF.
 *
 * `pageNumber` alone is not that page. Whether it happens to be depends on
 * which producer filled the entry: the repositioning pass overwrites it with
 * the uploaded page, while an entry seeded from value extraction leaves the
 * document version's own page there and reports the uploaded page in
 * `ingestedPageNumber`. Reading both in this order is correct for either
 * producer (see `ContractDocument.repositionedFieldsCacheKey`).
 */
export const getUploadedPageNumber = (
  field: RepositionedFieldLike
): number | null => field.ingestedPageNumber ?? field.pageNumber ?? null;

/**
 * Rewrites each field's `pageNumber` to the uploaded-PDF page, so callers can
 * filter, sort and draw on a single numbering. Fields already carrying the
 * uploaded page are returned untouched.
 *
 * Use this on any field list read out of `GetIngestionDocumentFragment.fields`
 * before comparing it against validation page numbers. It is what keeps a
 * validation on the second contract document of a merged upload from matching
 * nothing (its fields restart at page 1 while the failure names, say, page 12).
 */
export const withUploadedPageNumbers = <T extends RepositionedFieldLike>(
  fields: T[]
): T[] =>
  fields.map((field) => {
    const pageNumber = getUploadedPageNumber(field);

    return pageNumber == null || pageNumber === field.pageNumber
      ? field
      : { ...field, pageNumber };
  });

/**
 * Finds the next extracted field for key-term evidence and normalizes its page
 * to uploaded-PDF numbering. Repeated selections cycle through matches in
 * stable uploaded-page/id order, mirroring validation-issue navigation.
 */
export const resolveKeyTermEvidenceField = ({
  activeFieldId,
  contractDocumentUuid,
  fields,
  mappingKeys,
}: {
  activeFieldId?: string;
  contractDocumentUuid: string;
  fields: unknown;
  mappingKeys: string[];
}): RepositionedFieldLike | undefined => {
  const documentFields =
    parseFieldsByContractDocument(fields)[contractDocumentUuid] ?? [];
  const requestedKeys = new Set(mappingKeys);
  const matches = withUploadedPageNumbers(documentFields)
    .filter(
      (
        field
      ): field is RepositionedFieldLike & {
        id: string;
        mappingKey: string;
        pageNumber: number;
      } =>
        field.id != null &&
        field.pageNumber != null &&
        field.mappingKey != null &&
        requestedKeys.has(field.mappingKey)
    )
    .sort((left, right) =>
      left.pageNumber !== right.pageNumber
        ? left.pageNumber - right.pageNumber
        : left.id.localeCompare(right.id)
    );

  if (matches.length === 0) return undefined;

  const normalizedActiveFieldId = activeFieldId
    ? normalizeFieldId(activeFieldId)
    : undefined;
  const currentIndex = normalizedActiveFieldId
    ? matches.findIndex(
        (field) => normalizeFieldId(field.id) === normalizedActiveFieldId
      )
    : -1;

  return matches[(currentIndex + 1) % matches.length];
};

/**
 * Canonical field id: repositioned fields carry an `{originalId}-repositioned`
 * id, but the PDF overlay rects (and validation clicks) key on the original id.
 * Both the field-highlight sets and `buildValidationFieldsMap` MUST normalize
 * the same way, or `errorFieldIds`/`successFieldIds` won't match the boxes.
 */
export const normalizeFieldId = (id: string): string =>
  id.replace(/-repositioned$/, '');

/**
 * Cycles extracted fields in persisted order, optionally scoped to one
 * contract document and a set of source pages. The selected uploaded-PDF page
 * uses `ingestedPageNumber` first so navigation matches overlay placement.
 */
export const getNextFieldSelection = (
  fields: FieldsByContractDocument | null | undefined,
  mappingKeys: string[],
  contractDocumentUuid: string | undefined,
  activeFieldId: string,
  pageNumbers?: number[]
): {
  activeFieldId: string;
  contractDocumentUuid: string | null;
  pageNumber: number | null;
} | null => {
  if (!fields || mappingKeys.length === 0) {
    return null;
  }

  const requestedKeys = new Set(mappingKeys);
  const requestedPages = pageNumbers?.length ? new Set(pageNumbers) : null;
  const entries = contractDocumentUuid
    ? ([[contractDocumentUuid, fields[contractDocumentUuid]]] as const)
    : Object.entries(fields);
  const matches: Array<{
    activeFieldId: string;
    contractDocumentUuid: string;
    pageNumber: number | null;
  }> = [];

  for (const [documentUuid, documentFields] of entries) {
    if (!Array.isArray(documentFields)) {
      continue;
    }

    for (const field of documentFields) {
      // `requestedPages` are uploaded-PDF pages, so the field's page has to be
      // read as one too — `pageNumber` on its own is the document version's
      // page for extraction-seeded entries and would match nothing.
      const uploadedPageNumber = getUploadedPageNumber(field);

      if (
        field.id == null ||
        field.mappingKey == null ||
        !requestedKeys.has(field.mappingKey) ||
        (requestedPages != null &&
          (uploadedPageNumber == null ||
            !requestedPages.has(uploadedPageNumber)))
      ) {
        continue;
      }

      matches.push({
        activeFieldId: normalizeFieldId(field.id),
        contractDocumentUuid: documentUuid,
        pageNumber: uploadedPageNumber,
      });
    }
  }

  if (matches.length === 0) {
    return null;
  }

  const currentIndex = matches.findIndex(
    (match) => match.activeFieldId === activeFieldId
  );

  return matches[(currentIndex + 1) % matches.length];
};

/** A single field box CompliancePDFViewer's `validationFieldsMap` expects. */
export interface ValidationField {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  contractDocumentUuid: string;
}

/**
 * Groups a document's repositioned fields (keyed by contract document uuid)
 * into the `Map<pageNumber, ValidationField[]>` CompliancePDFViewer draws its
 * box overlays from. Strips the `-repositioned` id suffix (a no-op for ids that
 * don't carry it) and skips fields without an id or page.
 *
 * Single source of truth shared by the compliance drawer, the embedded SkySlope
 * drawer, and the agent compliance review page — each previously built this map
 * inline.
 */
export function buildValidationFieldsMap(
  fields: FieldsByContractDocument | null | undefined
): Map<number, ValidationField[]> {
  const map = new Map<number, ValidationField[]>();
  if (!fields) {
    return map;
  }

  for (const [contractDocumentUuid, contractDocumentFields] of Object.entries(
    fields
  )) {
    for (const field of asFieldArray(contractDocumentFields)) {
      const id = field.id == null ? undefined : normalizeFieldId(field.id);
      // The viewer draws on uploaded PDF pages, so key on the page the field
      // was read from and fall back to `pageNumber` for older extractions.
      const pageNumber = getUploadedPageNumber(field);
      if (!id || pageNumber == null) {
        continue;
      }
      const list = map.get(pageNumber) ?? [];
      list.push({
        id,
        x: field.x ?? 0,
        y: field.y ?? 0,
        width: field.width ?? 0,
        height: field.height ?? 0,
        pageNumber,
        contractDocumentUuid,
      });
      map.set(pageNumber, list);
    }
  }

  return map;
}

/**
 * The field ids a single validation item targets: fields whose `mappingKey` is
 * one of the item's `mappingKeys`, scoped to the item's contract document (when
 * set) and — when the item has page attribution — to those failing pages.
 *
 * This mirrors the field-matching the compliance drawer
 * (`ComplianceDrawer.tsx`) and the extension's `useDocumentValidationIssues`
 * run; it's extracted here so every surface (including the agent compliance
 * review page) shares one implementation.
 */
export function getValidationItemFieldIds(
  // Only the fields read, so `BaseValidationItem.onItemClick`'s args suffice.
  item: Pick<
    ValidationItem,
    'mappingKeys' | 'pageNumbers' | 'contractDocumentUuid'
  >,
  fields: FieldsByContractDocument | null | undefined
): string[] {
  if (!fields) {
    return [];
  }

  const mappingKeys = new Set(item.mappingKeys ?? []);
  if (mappingKeys.size === 0) {
    return [];
  }

  const failingPages = item.pageNumbers?.length
    ? new Set(item.pageNumbers)
    : null;

  // Scope to the item's contract document when known, else search all of them.
  const scopedEntries =
    item.contractDocumentUuid != null
      ? [
          [
            item.contractDocumentUuid,
            fields[item.contractDocumentUuid],
          ] as const,
        ]
      : Object.entries(fields);

  const ids = new Set<string>();
  for (const [, contractDocumentFields] of scopedEntries) {
    for (const field of asFieldArray(contractDocumentFields)) {
      if (field.id == null || field.mappingKey == null) {
        continue;
      }
      if (!mappingKeys.has(field.mappingKey)) {
        continue;
      }
      // `item.pageNumbers` index the uploaded PDF, so the field's page has to
      // be read as one too.
      const uploadedPageNumber = getUploadedPageNumber(field);
      if (
        failingPages &&
        (uploadedPageNumber == null || !failingPages.has(uploadedPageNumber))
      ) {
        continue;
      }
      // Normalize the same way buildValidationFieldsMap does, so these ids
      // match the PDF overlay rects' ids.
      ids.add(normalizeFieldId(field.id));
    }
  }

  return Array.from(ids);
}

/** A callout is one line of 12px text with padding. */
const CALLOUT_HEIGHT_PX = 26;
/** Each further line of a wrapped callout. */
const CALLOUT_LINE_HEIGHT_PX = 18;
/** Roughly what one line of a wrapped, 240px callout holds. */
const CALLOUT_CHARS_PER_LINE = 36;
/**
 * The two-part body `splitSuggestionOnNewline` draws: 14px text in 12px
 * padding, then a `SUGGESTED:` row. Its lines are shorter and taller.
 */
const SUGGESTION_CALLOUT = {
  charsPerLine: 27,
  lineHeight: 18,
  padding: 24,
  rowGap: 4,
  rowHeight: 21,
  suggestionCharsPerLine: 13,
} as const;
/** `CompliancePDFViewer` caps the bubble at this width. */
const CALLOUT_MAX_WIDTH_PX = 240;
/**
 * Page size to assume when the caller does not measure one. Only the ratio of
 * callout to page matters, and a rendered page is usually near this.
 */
const ASSUMED_PAGE_WIDTH_PX = 800;
const ASSUMED_PAGE_HEIGHT_PX = 1000;

/** A field's box, in the page's normalized coordinates. */
export interface CalloutAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * How the viewer draws callout text, which sets a callout's height: see
 * `wrapFieldMessages` and `splitSuggestionOnNewline` on the viewer.
 */
export interface CalloutLayout {
  splitSuggestion: boolean;
  wrap: boolean;
  /**
   * Where the viewer first places a callout: right of its field and centred
   * on it (the default), or above it and centred on it.
   */
  placement?: 'right' | 'top';
}

interface PixelBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * A callout's height in page pixels. Its size is fixed in pixels; the page's
 * is not.
 */
const calloutHeight = (message: string, layout: CalloutLayout): number => {
  const lines = (text: string, perLine: number): number =>
    Math.max(1, Math.ceil(text.length / perLine));
  const separator = layout.splitSuggestion ? message.indexOf('\n') : -1;

  // The two-part body wraps whether or not the other callouts do.
  if (separator !== -1) {
    const rule = message.slice(0, separator);
    const suggestion = message.slice(separator + 1);
    const { charsPerLine, lineHeight, padding, rowGap, rowHeight } =
      SUGGESTION_CALLOUT;

    return (
      padding +
      lines(rule, charsPerLine) * lineHeight +
      rowGap +
      lines(suggestion, SUGGESTION_CALLOUT.suggestionCharsPerLine) * rowHeight
    );
  }

  if (!layout.wrap) {
    return CALLOUT_HEIGHT_PX;
  }

  return (
    CALLOUT_HEIGHT_PX +
    (lines(message, CALLOUT_CHARS_PER_LINE) - 1) * CALLOUT_LINE_HEIGHT_PX
  );
};

/**
 * Where a callout lands in page pixels, as Popper first tries it: beside its
 * field (`layout.placement`) and centred on it.
 */
const calloutBox = (
  field: CalloutAnchor,
  message: string,
  page: { width: number; height: number },
  layout: CalloutLayout
): PixelBox => {
  const height = calloutHeight(message, layout);

  return match(layout.placement ?? 'right')
    .with('right', () => {
      const left = (field.x + field.width) * page.width;
      const centerY = (field.y + field.height / 2) * page.height;

      return {
        bottom: centerY + height / 2,
        left,
        right: left + CALLOUT_MAX_WIDTH_PX,
        top: centerY - height / 2,
      };
    })
    .with('top', () => {
      const centerX = (field.x + field.width / 2) * page.width;
      const bottom = field.y * page.height;

      return {
        bottom,
        left: centerX - CALLOUT_MAX_WIDTH_PX / 2,
        right: centerX + CALLOUT_MAX_WIDTH_PX / 2,
        top: bottom - height,
      };
    })
    .exhaustive();
};

const overlaps = (a: PixelBox, b: PixelBox): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/**
 * Whether two fields' callouts would cover each other on a page of this
 * rendered size, modelled as {@link pickMessageFieldIdsForPage} models them.
 */
export const calloutsCollide = (
  a: { field: CalloutAnchor; message: string },
  b: { field: CalloutAnchor; message: string },
  page: { width: number; height: number },
  layout: CalloutLayout
): boolean =>
  overlaps(
    calloutBox(a.field, a.message, page, layout),
    calloutBox(b.field, b.message, page, layout)
  );

/**
 * The box a selected field's callout points at: the field, grown to take in
 * every other selected field within one callout's reach of it, again until
 * none is left in reach. Popper places the callout beside this box, on
 * whichever side fits, so it never covers the selection it describes. A
 * selected field out of reach stays out, so a spread selection still points
 * at a real field rather than at the middle of the page.
 */
export const selectionCalloutAnchor = (
  field: CalloutAnchor,
  message: string,
  otherSelected: readonly CalloutAnchor[],
  page: { width: number; height: number },
  layout: CalloutLayout
): CalloutAnchor => {
  const reachX = CALLOUT_MAX_WIDTH_PX / page.width;
  const reachY = calloutHeight(message, layout) / page.height;
  let anchor = field;
  let rest = [...otherSelected];

  for (;;) {
    const current = anchor;
    const inReach = rest.filter(
      (other) =>
        other.x < current.x + current.width + reachX &&
        current.x - reachX < other.x + other.width &&
        other.y < current.y + current.height + reachY &&
        current.y - reachY < other.y + other.height
    );

    if (inReach.length === 0) {
      return anchor;
    }

    const boxes = [current, ...inReach];
    const left = Math.min(...boxes.map((box) => box.x));
    const top = Math.min(...boxes.map((box) => box.y));
    const right = Math.max(...boxes.map((box) => box.x + box.width));
    const bottom = Math.max(...boxes.map((box) => box.y + box.height));

    anchor = { height: bottom - top, width: right - left, x: left, y: top };
    rest = rest.filter((other) => !inReach.includes(other));
  }
};

/**
 * Picks which of one page's fields should paint their message callout when the
 * viewer is thinning them out: the FIRST field per distinct message, plus every
 * explicitly-selected field.
 *
 * A single rule routinely matches many fields on one page (an initials box per
 * paragraph), and painting a callout on each one stacks them into an unreadable
 * pile over the document (BLO-13495). One callout per message per page keeps the
 * page legible while still telling the agent every distinct thing that is wrong
 * on it; the fields left uncaptioned keep their highlight and reveal their
 * message on hover.
 *
 * "First" is top-to-bottom, then left-to-right — the reading order of the page,
 * so the surviving callout is the one an agent's eye reaches first rather than
 * whichever field the extractor happened to emit first.
 *
 * Selected fields are processed ahead of the rest, so when the agent clicks an
 * issue in the rail its own fields caption themselves and still consume their
 * message's slot — the page never shows the same message twice.
 *
 * Two distinct messages whose callouts would overlap are thinned too: the
 * later field in reading order keeps its highlight and reveals its message on
 * hover. Each callout is modelled where Popper first tries it, right of its
 * field and centred on it, so two fields with nearby right edges collide even
 * when their left edges are far apart. Findings at opposite margins of the
 * same line are far enough apart to keep both, and a selected field is never
 * dropped this way — the agent asked for that one.
 *
 * @param fields Fields on this page, in any order, in the page's normalized
 *   coordinates.
 * @param messageForField Resolves a field's callout text; `undefined` for fields
 *   with nothing to say (they can never win a slot).
 * @param selectedFieldIds Field ids of the issue the agent has selected, if any.
 * @param pageSize The page's rendered size in pixels. Field coordinates are
 *   normalized but a callout is drawn at a fixed pixel size, so the two only
 *   compare once the page's own dimensions are known — a narrow page fits fewer
 *   callouts across a line than a wide one.
 * @param layout How the viewer draws callout text. A wrapped or two-part
 *   callout is taller than one line.
 * @returns The subset of field ids whose callout should render unprompted.
 */
export function pickMessageFieldIdsForPage(
  fields: Array<CalloutAnchor & { id: string }>,
  messageForField: (fieldId: string) => string | undefined,
  selectedFieldIds: ReadonlySet<string> = new Set(),
  pageSize?: { width: number; height: number },
  layout: CalloutLayout = { splitSuggestion: false, wrap: false }
): Set<string> {
  const pageWidth =
    pageSize?.width != null && pageSize.width > 0
      ? pageSize.width
      : ASSUMED_PAGE_WIDTH_PX;
  const pageHeight =
    pageSize?.height != null && pageSize.height > 0
      ? pageSize.height
      : ASSUMED_PAGE_HEIGHT_PX;
  const inReadingOrder = [...fields].sort(
    (a, b) => a.y - b.y || a.x - b.x || a.id.localeCompare(b.id)
  );
  // Selected fields first so they claim their message's slot for the page.
  const byPriority = [
    ...inReadingOrder.filter((field) => selectedFieldIds.has(field.id)),
    ...inReadingOrder.filter((field) => !selectedFieldIds.has(field.id)),
  ];

  const seenMessages = new Set<string>();
  const page = { height: pageHeight, width: pageWidth };
  const captioned: PixelBox[] = [];
  const fieldIds = new Set<string>();
  for (const field of byPriority) {
    const message = messageForField(field.id);
    if (message == null || seenMessages.has(message)) {
      continue;
    }

    const box = calloutBox(field, message, page, layout);
    const collides = captioned.some((placed) => overlaps(box, placed));
    // Skipped without claiming the message, so the same message can still
    // caption itself further down the page.
    if (collides && !selectedFieldIds.has(field.id)) {
      continue;
    }

    seenMessages.add(message);
    captioned.push(box);
    fieldIds.add(field.id);
  }

  return fieldIds;
}

/** Field-highlight sets the PDF viewer consumes, derived from validation items. */
export interface ComplianceFieldHighlights {
  /** Field ids to paint as failed (errors). */
  errorFieldIds: string[];
  /** Field ids to paint as passed (successes), with errors removed. */
  successFieldIds: string[];
  /** Per-field message for failed fields (the callout text). */
  fieldMessages: Record<string, string>;
}

/**
 * Builds the error/success field id sets + per-field error messages from the
 * split validation items (the output of `getValidationResultsForDocument`) and
 * the document's repositioned fields. Error wins: a field flagged by both a
 * failed and a passed result is only an error.
 */
export function buildComplianceFieldHighlights(
  errors: ValidationItem[],
  successes: ValidationItem[],
  fields: FieldsByContractDocument | null | undefined
): ComplianceFieldHighlights {
  const errorFieldIds = new Set<string>();
  const fieldMessages: Record<string, string> = {};
  for (const error of errors) {
    for (const fieldId of getValidationItemFieldIds(error, fields)) {
      errorFieldIds.add(fieldId);
      if (fieldMessages[fieldId] == null) {
        fieldMessages[fieldId] = error.message;
      }
    }
  }

  const successFieldIds = new Set<string>();
  for (const success of successes) {
    for (const fieldId of getValidationItemFieldIds(success, fields)) {
      if (!errorFieldIds.has(fieldId)) {
        successFieldIds.add(fieldId);
      }
    }
  }

  return {
    errorFieldIds: Array.from(errorFieldIds),
    successFieldIds: Array.from(successFieldIds),
    fieldMessages,
  };
}
